import { EditOutlined, Groups, MoreVertOutlined } from '@mui/icons-material';
import { Box, Menu, MenuItem, MenuList, Stack, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import { deleteProjectFestival } from '@/domain/project-festival/actions/delete-project-festival.action';
import { ProjectFestival } from '@/domain/project-festival/entities/project-festival.entity';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { useDialogConfirm } from '@/ui/components/confirm-dialog/useDialogConfirm';
import { useAppDispatch } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

export function HeaderButtons({
  project,
  onClickStakeholders,
  onClickDuplicateProject,
  onClickExportReport,
  onClickImportAnswers,
}: {
  project: ProjectFestival;
  onClickStakeholders: () => void;
  onClickDuplicateProject: () => void;
  onClickExportReport: () => void;
  onClickImportAnswers: () => void;
}) {
  const dispatch = useAppDispatch();
  const dialogConfirm = useDialogConfirm();
  const navigate = useNavigate();

  const onClickDelete = () => {
    askConfirmationThenDeleteProject();
  };

  const askConfirmationThenDeleteProject = async () => {
    const response = await dialogConfirm({
      title: translate('DeleteProject.Message.confirmation'),
    });
    if (response === 'confirmed') {
      executeDeleteProject();
    }
  };

  const executeDeleteProject = async () => {
    if (!project?.id) {
      return;
    }
    try {
      await dispatch(deleteProjectFestival(project.id)).unwrap();
      navigate('/app/projects/festival');
    } catch (error) {
      console.error(error); // TODO: this error should not be handled?
    }
  };

  const navigateToEdit = async () => {
    if (!project?.id) {
      return;
    }
    navigate(`/app/projects/festival/${project.id}/edit`);
  };

  return (
    <Stack direction={'row'} spacing={1} alignItems={'start'}>
      <Tooltip
        title={translate('Projects.stakeholders-button-info')}
        placement="bottom"
      >
        {/* hack: box is used to make tooltip visible */}
        <Box>
          <FSPrimaryButton startIcon={<Groups />} onClick={onClickStakeholders}>
            {translate('InviteExternalToFill.stakeholders')}
          </FSPrimaryButton>
        </Box>
      </Tooltip>
      <Tooltip
        title={translate('Projects.edit-project-button-info')}
        placement="bottom"
      >
        {/* hack: box is used to make tooltip visible */}
        <Box>
          <FSPrimaryButton
            startIcon={<EditOutlined />}
            onClick={navigateToEdit}
          >
            {translate('Form.edit')}
          </FSPrimaryButton>
        </Box>
      </Tooltip>
      <ActionsMenu
        onClickDelete={onClickDelete}
        onClickDuplicate={onClickDuplicateProject}
        onClickExportReport={onClickExportReport}
        onClickImportAnswers={onClickImportAnswers}
      />
    </Stack>
  );
}

function ActionsMenu({
  onClickDelete,
  onClickDuplicate,
  onClickExportReport,
  onClickImportAnswers,
}: {
  onClickDelete: () => void;
  onClickDuplicate: () => void;
  onClickExportReport: () => void;
  onClickImportAnswers: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<undefined | HTMLElement>(undefined);

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(undefined);
  };

  const onClickItem = (actionToCall: () => void) => {
    onClose();
    actionToCall();
  };

  return (
    <>
      <FSSecondaryButton startIcon={<MoreVertOutlined />} onClick={onClick}>
        {translate('Form.actions')}
      </FSSecondaryButton>
      <Menu open={Boolean(anchorEl)} onClose={onClose} anchorEl={anchorEl}>
        <MenuList>
          <MenuItem onClick={() => onClickItem(onClickDuplicate)}>
            {translate('DuplicateProject.Form.duplicate')}
          </MenuItem>
          <MenuItem onClick={() => onClickItem(onClickDelete)}>
            {translate('Form.delete')}
          </MenuItem>
          <MenuItem onClick={() => onClickItem(onClickExportReport)}>
            {translate('ReportExporter.menu-item-export')}
          </MenuItem>
          <MenuItem onClick={() => onClickItem(onClickImportAnswers)}>
            {translate('ModalImportAnswers.menu-item-title')}
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
}
